import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/auth_api';
import { useAuth } from '@/lib/auth_context';
import { Loader2, BookOpen } from 'lucide-react';
import { HierarchyBackButton } from '@/components/navigation/HierarchyBackButton';

/**
 * Handles the OAuth callback redirect from Emergent Auth.
 * URL fragment will look like: /#session_id=<token>
 * We exchange the session_id with our backend, which sets the HttpOnly cookie
 * and creates / updates the user record, then we navigate to the main app.
 *
 * iter55g66 — On exchange failure the previous implementation silently
 * fell through to `/app`, where `RequireAuth` immediately kicked the
 * user back to `/login`. From the user's perspective this looked like
 * "Google authorisation threw an error and just recycled the login."
 * We now:
 *   • extract the real error text (Emergent typically returns
 *     `user_data_not_found` when the session_id was consumed twice or
 *     expired) so the LoginPage banner can surface it
 *   • redirect to `/login?auth_error=<msg>` so the user sees a real
 *     message + a "Try again" button, rather than an infinite loop.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  // CRITICAL: synchronous guard at top of effect to prevent double-runs in StrictMode.
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      // Landed on /#... without a session_id — send them back to sign in
      // rather than a protected route that just bounces again.
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const r = await authApi.session(sessionId);
        setUser(r.user);
        // Clean URL: strip the fragment, then navigate to the app.
        window.history.replaceState({}, '', '/app');
        navigate('/app', { replace: true, state: { user: r.user } });
      } catch (err) {
        console.error('Auth exchange failed', err);
        // Pull the readable message off the axios error. Emergent
        // returns { detail: "OAuth exchange failed: 404 {...}" }; strip
        // the noisy status prefix so the banner reads clean.
        let msg = 'Google sign-in could not be completed. Please try again.';
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') {
          if (detail.includes('user_data_not_found')) {
            msg = 'Your Google sign-in link expired before it reached us. This usually means the page was refreshed or opened twice — please tap "Continue with Google" again.';
          } else if (detail.length < 240) {
            msg = detail;
          }
        }
        // Clear the fragment so a refresh doesn't retry the same dead id.
        window.history.replaceState({}, '', '/login');
        navigate(`/login?auth_error=${encodeURIComponent(msg)}`, { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="relative">
          <div className="flex h-14 w-14 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-panel-active">
            <BookOpen className="h-6 w-6" strokeWidth={2.25} />
          </div>
          <Loader2 className="absolute -bottom-1 -right-1 h-4 w-4 animate-spin text-primary" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">The Loom</div>
          <div className="text-lg font-semibold">Signing you in…</div>
        </div>
        <HierarchyBackButton to="/login" label="Back to sign in" testId="auth-callback-hierarchy-back" />
      </div>
    </div>
  );
}
